import React, { useEffect, useRef } from "react";

export const CODE_LENGTH = 6;
export const emptyCode = () => Array(CODE_LENGTH).fill("");

/**
 * One box per digit of an emailed code. Whatever lands in a box — a typed digit, a paste, or a
 * whole code from the browser's one-time-code autofill — fills it and the boxes after it.
 */
// onEnter: called on Enter in any box.
const OtpCodeInput = ({ code, onChange, onEnter, boxClassName }) => {
  const inputRefs = useRef([]);
  const isEmpty = code.every((digit) => !digit);

  // Ready to type on first show and after a resend clears the boxes.
  useEffect(() => {
    if (isEmpty) inputRefs.current[0]?.focus();
  }, [isEmpty]);

  const fillFrom = (index, text) => {
    const all = text.replace(/\D/g, "");
    if (!all) return; // nothing usable (a letter, a space): leave the box as it was
    // A whole code — pasted or autofilled — fills every box, whichever box it landed in.
    const start = all.length >= CODE_LENGTH ? 0 : index;
    const digits = all.slice(0, CODE_LENGTH - start);
    const next = [...code];
    digits.split("").forEach((digit, i) => (next[start + i] = digit));
    onChange(next);
    inputRefs.current[Math.min(start + digits.length, CODE_LENGTH - 1)]?.focus();
  };

  const handleChange = (index, value) => {
    if (!value) {
      // Deleted.
      const next = [...code];
      next[index] = "";
      onChange(next);
      return;
    }
    // Typing beside a digit that wasn't selected adds one character: keep the new one.
    const old = code[index];
    const typed = old && value.length === old.length + 1 ? (value.startsWith(old) ? value.slice(old.length) : value.slice(0, 1)) : value;
    fillFrom(index, typed);
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Enter") onEnter?.();
    if (e.key === "Backspace" && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (index, e) => {
    e.preventDefault();
    fillFrom(index, e.clipboardData.getData("text"));
  };

  return (
    <div className="flex gap-3 justify-center mb-4">
      {code.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={index === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${index + 1}`}
          value={digit}
          onFocus={(e) => e.target.select()}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={(e) => handlePaste(index, e)}
          className={boxClassName}
        />
      ))}
    </div>
  );
};

export default OtpCodeInput;
